
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';
import ConfettiEffect from '@/components/ConfettiEffect';

interface OnboardingThankYouProps {
  onFollowX: () => void;
  onClose: () => void;
  showConfetti: boolean;
  isExistingUser?: boolean;
}

const OnboardingThankYou = ({ onFollowX, onClose, showConfetti, isExistingUser = false }: OnboardingThankYouProps) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-500 via-purple-400 to-indigo-500 flex items-center justify-center px-4 animate-fade-in">
      <ConfettiEffect 
        trigger={showConfetti} 
        type="complete" 
        origin={{ x: 50, y: 30 }}
      />
      
      <div className="w-full max-w-2xl animate-scale-in">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl animate-bounce-in">
            <img 
              src="/lovable-uploads/176a7476-cd67-4fa3-8321-efb0ad50558c.png" 
              alt="Show Up! Logo"
              className="w-12 h-12 object-contain"
            />
          </div>
          <h1 className="text-5xl font-black text-white mb-4 animate-slide-up">
            {isExistingUser ? "You're already registered! 🎉" : "You're in! 🎉"}
          </h1>
          <h2 className="text-2xl font-bold text-white mb-6 animate-slide-up">
            {isExistingUser 
              ? "Thank you so much for your interest!" 
              : "Welcome to the Show Up! experiment"
            }
          </h2>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-3xl p-8 shadow-2xl mb-8 animate-slide-up">
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            {isExistingUser 
              ? "We already have your information and you're all set! Check your email for upcoming news and updates."
              : "Thanks for joining us on this journey. You're now part of a community that believes in showing up, even when it's hard."
            }
          </p>
          
          <div className="mb-6">
            <p className="text-lg font-semibold text-gray-800 mb-4">
              {isExistingUser 
                ? "As a reminder, you'll be receiving:" 
                : "Check your email in the next few minutes for:"
              }
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 animate-slide-up">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-gray-700">Your PACT Design Guide (bonus download)</span>
              </div>
              <div className="flex items-center gap-3 animate-slide-up">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-gray-700">Behind-the-scenes updates from our founder</span>
              </div>
              <div className="flex items-center gap-3 animate-slide-up">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-gray-700">Early access notification when we launch</span>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-700 mb-6">
            Ready to show up? We'll be in touch soon.
          </p>

          <p className="text-sm text-gray-500 italic mb-4">
            P.S. Follow us on X @showupagain for daily motivation and updates
          </p>

          <p className="text-sm text-gray-600 border-t pt-4">
            Questions? Contact us at{' '}
            <a 
              href="mailto:contact@showupagain.com" 
              className="text-purple-600 hover:text-purple-800 font-medium underline"
            >
              contact@showupagain.com
            </a>
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 animate-slide-up">
          <Button
            onClick={onFollowX}
            className="flex-1 bg-black hover:bg-gray-800 text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Follow us on X
          </Button>
          
          <Button
            onClick={onClose}
            className="flex-1 bg-gradient-to-r from-purple-600 to-teal-500 hover:from-purple-700 hover:to-teal-600 text-white py-4 rounded-xl font-black text-lg transition-all duration-300 hover:scale-105"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingThankYou;
